import { Tooltip } from "@mui/material";
import { CmLink } from "./CmLink";
import Badge from "../ui/badge/Badge";
import { ArrowUpIcon, ArrowDownIcon, InfoIcon } from "../../icons";
import { formatCurrency, formatCurrencyAdaptive } from "../../utils/formatCurrency";

type StatPolarity = "positive" | "negative" | "neutral";
type StatTone = "good" | "warn" | "bad";

interface StatCardProps {
  label: string;
  value: number;
  previousValue?: number;
  isCurrency?: boolean;
  currency?: "USD" | "EUR";
  valueSymbol?: string;
  deltaSymbol?: string;
  // Does an INCREASE in this metric read as good or bad? Drives chip color + arrow.
  polarity?: StatPolarity;
  // Health colour for the value itself (e.g. Win Rate vs a target). Independent of the delta chip.
  tone?: StatTone;
  size?: "hero" | "default";
  tooltip?: string;
  href?: string;
}

const TONE_CLASS: Record<StatTone, string> = {
  good: "text-success-600 dark:text-success-500",
  warn: "text-warning-600 dark:text-orange-400",
  bad: "text-error-600 dark:text-error-500",
};

export default function StatCard({
  label,
  value,
  previousValue,
  isCurrency,
  currency = "USD",
  valueSymbol = "",
  deltaSymbol = "",
  polarity = "positive",
  tone,
  size = "default",
  tooltip,
  href,
}: StatCardProps) {
  const delta = previousValue !== undefined ? Math.round(value - previousValue) : undefined;
  const showDelta = delta !== undefined && delta !== 0;
  const isHero = size === "hero";

  // Good vs. bad is intent (polarity) × direction — NOT the raw sign of the delta.
  // "neutral" metrics (e.g. inflow volume we don't control) just show movement in gray.
  const badgeColor: "success" | "error" | "light" =
    delta === undefined || polarity === "neutral"
      ? "light"
      : delta > 0 === (polarity === "positive")
        ? "success"
        : "error";
  const badgeIcon = delta !== undefined && delta > 0 ? <ArrowUpIcon /> : <ArrowDownIcon />;
  const deltaText =
    delta === undefined
      ? ""
      : isCurrency
        ? `${delta > 0 ? "+" : "-"}${formatCurrency(Math.abs(delta), currency, true, 0)}`
        : `${delta > 0 ? "+" : "-"}${Math.abs(delta)}${deltaSymbol}`;

  const valueStr = isCurrency ? formatCurrencyAdaptive(value, currency) : `${value.toLocaleString()}${valueSymbol}`;
  const valueClass = isHero ? "text-title-md" : "text-2xl";
  const toneClass = tone ? TONE_CLASS[tone] : "text-gray-800 dark:text-white/90";

  return (
    <CardShell href={href} hero={isHero}>
      <div className="flex min-h-[2.5em] items-start justify-between gap-1">
        <span className="line-clamp-2 text-sm text-gray-500 dark:text-gray-400">
          {label}
          {tooltip && (
            <Tooltip title={tooltip} arrow placement="top">
              <span className="ml-1 inline-flex cursor-help align-middle">
                <InfoIcon className="size-3.5 text-gray-400" />
              </span>
            </Tooltip>
          )}
        </span>
        {href && (
          <svg
            className="size-4 shrink-0 text-gray-300"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden
          >
            <path d="M7 4l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
      <div className="mt-2">
        <h4 className={`font-bold ${toneClass} ${valueClass}`}>{valueStr}</h4>
        {/* Trend always sits BELOW the value in a reserved row that is ALWAYS rendered, so every
            card's value shares one baseline across a row whether or not it has a delta. Holds the
            delta chip when a prior period exists, otherwise stays empty (but keeps its height). */}
        <div className="mt-1.5 flex min-h-[1.75rem] items-center">
          {showDelta && (
            <Badge color={badgeColor} startIcon={badgeIcon}>
              {deltaText}
            </Badge>
          )}
        </div>
      </div>
    </CardShell>
  );
}

function CardShell({ href, hero, children }: { href?: string; hero: boolean; children: React.ReactNode }) {
  const pad = hero ? "p-5 md:p-7" : "p-4 md:p-5";
  const cls = `flex h-full flex-col justify-between rounded-2xl border border-gray-200 bg-white ${pad} dark:border-gray-800 dark:bg-white/[0.03] ${
    hero ? "shadow-sm" : ""
  } ${href ? "transition hover:border-brand-300 hover:shadow-md" : ""}`;
  return href ? (
    <CmLink to={href} className={cls}>
      {children}
    </CmLink>
  ) : (
    <div className={cls}>{children}</div>
  );
}
